import { CrudFilters } from "@pankod/refine-core";
import dayjs from "dayjs";

import {
  FeatureFlagDateFilterValue,
  IFeatureFlagFilters,
} from "interfaces/feature-flags";
import { normalizeFeatureFlagListInput } from "./feature-flags.payload";

export const FEATURE_FLAGS_PAGINATION_DEFAULTS = {
  initialCurrent: 1,
  initialPageSize: 5,
  hasPagination: true,
};

type FilterValue = string | boolean | string[] | null | undefined;

const isFilledFilterValue = (value: FilterValue): boolean => {
  if (Array.isArray(value)) {
    return value.length > 0;
  }

  if (typeof value === "boolean") {
    return true;
  }

  if (typeof value === "string") {
    return value.trim() !== "";
  }

  return false;
};

const normalizeDateFilter = (
  value?: FeatureFlagDateFilterValue | null,
): string | null => {
  if (!value) {
    return null;
  }

  if (dayjs.isDayjs(value) || typeof (value as any).format === "function") {
    return (value as { format: (format: string) => string }).format(
      "YYYY-MM-DD",
    );
  }

  if (value instanceof Date) {
    return value.toISOString().split("T")[0];
  }

  return typeof value === "string" ? value.trim() : null;
};

const addFilter = (
  filters: CrudFilters,
  field: string,
  value: FilterValue,
): void => {
  if (!isFilledFilterValue(value)) {
    return;
  }

  filters.push({
    field,
    operator: "eq",
    value,
  });
};

export const buildFeatureFlagFilters = (
  values: IFeatureFlagFilters,
): CrudFilters => {
  const filters: CrudFilters = [];
  const targetRepos = normalizeFeatureFlagListInput(values.target_repos);
  const tags = normalizeFeatureFlagListInput(values.tags);

  addFilter(filters, "name", values.name?.trim());
  addFilter(filters, "key", values.key?.trim());
  addFilter(filters, "enabled", values.enabled);
  addFilter(filters, "flag_type", values.flag_type);
  addFilter(filters, "contexts", values.contexts);
  addFilter(filters, "target_repos", targetRepos);
  addFilter(filters, "tags", tags);
  addFilter(filters, "expires_at", normalizeDateFilter(values.expires_at));

  return filters;
};

export const buildFeatureFlagSearchFormValues = (
  filters: CrudFilters,
): Partial<IFeatureFlagFilters> => {
  const getStringListFilterValue = (
    value: unknown,
  ): string | undefined => {
    if (Array.isArray(value)) {
      return value.join(", ");
    }

    if (typeof value === "string") {
      return value;
    }

    return undefined;
  };

  return filters.reduce<Partial<IFeatureFlagFilters>>((values, filter) => {
    if (!("field" in filter)) {
      return values;
    }

    const { field, value } = filter;

    switch (field) {
      case "name":
        values.name = typeof value === "string" ? value : String(value);
        break;
      case "key":
        values.key = typeof value === "string" ? value : String(value);
        break;
      case "enabled":
        values.enabled = value === true || value === "true";
        break;
      case "flag_type":
        values.flag_type = value as IFeatureFlagFilters["flag_type"];
        break;
      case "contexts":
        values.contexts = value as IFeatureFlagFilters["contexts"];
        break;
      case "target_repos":
        values.target_repos = getStringListFilterValue(value);
        break;
      case "tags":
        values.tags = getStringListFilterValue(value);
        break;
      case "expires_at":
        values.expires_at =
          typeof value === "string" && value ? dayjs(value) : undefined;
        break;
      default:
        break;
    }

    return values;
  }, {});
};
